import { CardGroup } from '@ps/isp-web-component-library';
import { Translation } from '@ps/web-shared-components';
import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type { Dispatch } from '@swf/core/store';
import { select } from '@swf/core/store';
import { ExpandablePanel, Overlay } from '@swf/sports/components';
import useMarketPolling from '@swf/sports/hooks/useMarketPolling';

import haveMarketsChanged from '../../../../utils/haveMarketsChanged';
import Title from '../../../Title/Title';
import Opportunity from '../Opportunity/Opportunity';

import { TXTWCL_Singles } from './SingleOpportunities.messages.json';
import styles from './SingleOpportunities.scss';
import SkeletonOpportunity from './SkeletonOpportunity';

const SingleOpportunities: React.FC = () => {
  const opportunities = useSelector(select.betSlip.singleOpportunities);
  const pendingSelectionCount = useSelector(select.betSlip.pendingSelectionCount);
  const isBetReceiptActive = useSelector(select.betSlip.isBetReceiptActive);
  const fetchStatus = useSelector(select.betSlip.fetchStatus);
  const currency = useSelector(select.currency.get);
  const dispatch = useDispatch<Dispatch>();
  const isFirstRender = useRef(true);

  /**
   * Refetch opportunities when:
   * * the user logs in or out
   */
  useEffect(() => {
    if (!isBetReceiptActive && fetchStatus === 'AUTH_UPDATE_PENDING' && !isFirstRender.current) {
      dispatch.betSlip.opportunitiesRequested();
    }
  }, [isBetReceiptActive, fetchStatus, dispatch.betSlip]);

  /**
   * Refetch opportunities when:
   * * the user switches from the bet receipt to the bet slip
   * * the user changes currency
   * * Skip this refetch on first render of component otherwise we request opportunities once on add reducer and once on first render
   */
  useEffect(() => {
    if (isBetReceiptActive || isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    dispatch.betSlip.opportunitiesRequested();
  }, [isBetReceiptActive, currency, dispatch.betSlip]);

  /**
   * Refetch opportunities when:
   * * the market prices have changed
   */
  const marketIds = opportunities.map(({ runnerId }) => runnerId.marketId);
  useMarketPolling({
    marketIds,
    onMarketDataReceived: (data) => {
      if (haveMarketsChanged(opportunities, data)) {
        dispatch.betSlip.opportunitiesRequested();
      }
    },
    skipAddingMarketIds: true
  });

  if (!opportunities.length && !pendingSelectionCount) {
    return null;
  }

  const skeletonCards = [...Array(pendingSelectionCount).keys()];

  return (
    <ExpandablePanel
      title={
        <Title count={opportunities.length + pendingSelectionCount || undefined}>
          <Translation {...TXTWCL_Singles} />
        </Title>
      }
    >
      <CardGroup isCondensed isPadded>
        {opportunities.map((opportunity) => (
          <div className={styles.cardGroup} key={opportunity.id}>
            <Overlay />
            <Opportunity opportunity={opportunity} />
          </div>
        ))}
        {skeletonCards.map((skeletonCard) => (
          <SkeletonOpportunity key={skeletonCard} />
        ))}
      </CardGroup>
    </ExpandablePanel>
  );
};

export default SingleOpportunities;
